import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCCContext } from '../CC/CCContext';
import GenericIconButton from '../GenericComponents/GenericIconButton';
import genericStyles from '../GenericComponents/GenericStyles.module.css';
import * as ccUtils from '../CC/utils';
import styles from './Record.module.css';
import ZoomedImage from './ZoomedImage';
import BasketManager from './BasketManager';
import ObjAssoc from './ObjAssoc';
import ObjSameTheme from './ObjSameTheme';

interface RecordProps {
  record: any;
}

interface Image {
  image: string;
  copyright: string;
  rank: string;
}

function replaceExtensionWithDzi(imageUrl: string): string {
  const urlWithoutExtension = imageUrl.substring(0, imageUrl.lastIndexOf('.'));
  return `${urlWithoutExtension}.dzi`;
}

function Record({ record }: RecordProps) {
  const { t } = useTranslation();
  const ctx = useCCContext();
  const filmStripRef = useRef<HTMLDivElement | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [noDziUrl, setNoDziUrl] = useState<string | null>(null)
  const [noImageUrl] = useState<string>(`${process.env.REACT_APP_IMAGE_BASE_URL}/nondisponible.cci`); // Set default no image URL as string
  const [filmStripImages, setFilmStripImages] = useState<Image[]>([]);
  const [, setError] = useState<Error | null>(null);
  const [currentIndex, setCurrentIndex] = useState<number>(0);

  useEffect(() => {
    if (record) {
      const images = Array.isArray(record?.data?.record?.image) ? record?.data?.record?.image : [record?.data?.record?.image];
      const sortedImages = images.filter(Boolean).sort((a: Image, b: Image) => parseInt(a.rank) - parseInt(b.rank)); // Filter out null or undefined images
      setFilmStripImages(sortedImages);
      setCurrentIndex(0);

      if (filmStripRef.current) {
        filmStripRef.current.scrollLeft = 0;
      }
      const defaultImageUrl = sortedImages.length > 0 ? `${process.env.REACT_APP_DZI_BASE_URL}${replaceExtensionWithDzi(sortedImages[0].image)}` : noImageUrl;

      setImageUrl(defaultImageUrl);
      if (sortedImages.length > 0) setNoDziUrl(sortedImages[0].image)
      /*
      if (sortedImages.length > 0) {
        var xhr = new XMLHttpRequest();
        xhr.onload = function() {
          var reader = new FileReader();
          reader.onloadend = function() {
            let dataUrl = reader.result as string
            setNoDziUrl(dataUrl);
          }
          reader.readAsDataURL(xhr.response);
        };
        let url = `${process.env.REACT_APP_IMAGE_BASE_URL}${sortedImages[0].image}`
        xhr.open('GET', url);
        xhr.responseType = 'blob';
        xhr.send();
      }*/
    }
  }, [record]);


  useEffect(() => {
    if (record?.data?.record?.title?.title) {
      document.title = `Collections | Musée des Arts et Metiers - ${record?.data?.record?.title?.title}`;
    }
  
    return () => {
      document.title = "Collections | Musée des Arts et Metiers";
      //console.log('useEffect close', document.title);
    };
  }, [record]);


  if (!record) {
    return <div>Aucun enregistrement</div>;
  }

  function getHyperlinks(record: any): JSX.Element | null {
    const hyperlinks = record?.data?.record?.hyperlink;
  
    if (!hyperlinks) return null;
  
    const renderLink = (link: { objnrlink: string; hyperlink: string }) => {
      const linkText = link.objnrlink;
      return (
        <a 
          key={link.objnrlink} 
          href={link.hyperlink} 
          target="_blank" 
          rel="noopener noreferrer" 
          className={styles.tagValue}
        >
          {linkText}
        </a>
      );
    };
  
    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{t("record_hyperlink")} : </span>
        {Array.isArray(hyperlinks) 
          ? hyperlinks.map((link, index) => (
              <span key={link.objnrlink}>
                {renderLink(link)}
                {index < hyperlinks.length - 1 && ', '}
              </span>
            ))
          : renderLink(hyperlinks)}
      </div>
    );
  }

  const handleScrollLeft = () => {
    const filmStripElement = filmStripRef.current;
    filmStripElement && filmStripElement.scrollBy({
      left: -300,
      behavior: 'smooth'
    });
  };

  const handleFilmStripImageClick = (image: Image, idx: number) => {
    const newImageFilename = replaceExtensionWithDzi(image.image);
    const newImageUrl = `${process.env.REACT_APP_DZI_BASE_URL}${newImageFilename}`;
    setImageUrl(newImageUrl);
    setCurrentIndex(idx);
  };

  const handleScrollRight = () => {
    const filmStripElement = filmStripRef.current;
    filmStripElement && filmStripElement.scrollBy({
      left: 300,
      behavior: 'smooth'
    });
  };

  function getDefaultRow(record: any, label: string, fieldName: string, defaultValue: string = "", separator: string = " : "): JSX.Element | null {
    function getNestedValue(obj: any, path: string): any {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }
  
    let fieldValue = getNestedValue(record?.data?.record, fieldName);
  
    if (Array.isArray(fieldValue)) {
      fieldValue = fieldValue.join(', ');
    } else if (typeof fieldValue === 'object' && fieldValue !== null) {
      fieldValue = JSON.stringify(fieldValue);
    }
  
    if (!fieldValue && !defaultValue) return null;
  
    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label}{separator}</span>
        <span id={fieldName}
          className={styles.tagValue}
          dangerouslySetInnerHTML={{ __html: fieldValue ?? defaultValue }}
        ></span>
      </div>
    );
  }
  
  //record: cc record
  //label: translated term in fromt of cc value
  //fieldName: cc tag
  //hyperlinkfield: 
  function getDefaultRowHyperlink(record: any, label: string, fieldName: string, hyperlinkfield: string = "", defaultValue: string = ""): JSX.Element | null {
    function getNestedValue(obj: any, path: string): any {
      return path.split('.').reduce((acc, part) => acc && acc[part], obj);
    }

    //console.log(fieldName);
    const fieldValue = getNestedValue(record?.data?.record, fieldName);
    //console.log(fieldValue);

    const handleOnClick = (tag: string, value: string, e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      ccUtils.customSearch(ctx, '/record/' + tag, '', '' + value + '', label + ' : ' + value);
    };

    if (!fieldValue && !defaultValue) return null;

    return (
      <div className={styles.row}>
        <span className={styles.taglabel}>{label} : </span>
        {fieldValue
          ? <span className={styles.authName} onClick={(e) => handleOnClick(fieldName, fieldValue, e)}>{fieldValue}</span>
          : defaultValue}
      </div>
    );
  }

  function getDefaultRowHyperlinkArray(
    record: any,
    label: string,
    fieldName: string,
    hyperlinkfield: string = "",
    defaultValue: string = ""
    ): JSX.Element | null {
      function getNestedValue(obj: any, path: string): any {
        return path.split('.').reduce((acc, part) => acc && acc[part], obj);
      }
    
      //console.log(fieldName);
      const fieldValue = getNestedValue(record?.data?.record, fieldName);
      //console.log(fieldValue);
    
      const handleOnClick = (tag: string, value: string, e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();
        ccUtils.customSearch(ctx, '/record/' + tag, '', '' + value + '', label + ' : ' + value);
      };
    
      // Als fieldValue leeg is en er is geen defaultValue, return null
      if (!fieldValue && !defaultValue) return null;
    
      // Zorg ervoor dat fieldValue een array is
      const fieldValuesArray = Array.isArray(fieldValue) ? fieldValue : [fieldValue];
    
      return (
        <div className={styles.row}>
          <span className={styles.taglabel}>{label} : </span>
          <div>
            {fieldValuesArray.map((value, index) => (
              <div key={index} style={{ marginLeft: '20px' }}>
                <span
                  className={styles.linkTagValue}
                  onClick={(e) => handleOnClick(fieldName, value, e)}
                >
                  {value}
                </span>
              </div>
            ))}
            {!fieldValuesArray.length && defaultValue}
          </div>
        </div>
      );
  }

  function getComponents(record: any) {
    //render components part of the current object
    const elements = record?.data?.record?.component;
    if (!elements) return null;

    const elementArray = Array.isArray(elements) ? elements : [elements];
    const rows = elementArray.map((element: any, index) => {
      const cname = element.componentname;
      const cnumber = element.componentnumber;
      return (
        <div className={styles.row} key={index} style={{ marginLeft: '20px' }}>
          <span className={styles.linkTagValue} style={{cursor: 'cursor'}}>{cname}, {cnumber}</span>
        </div>
      );
    });

    if (rows.length === 0) return null;

    return <>
      <div className={styles.taglabel} style={{ marginTop: '5px', marginBottom: '10px' }}>
      Cet objet est composé de plusieurs éléments :
      </div>
      <div>
        {rows}
      </div>
    </>
  }

  function getPersonnes(record: any, tag: keyof typeof record.data.record) {
    const elements = record?.data?.record?.[tag];
    if (!elements) return null;

    const elementArray = Array.isArray(elements) ? elements : [elements];

    elementArray.sort((a, b) => {
      const keyA = a.roletypeid + ',' + a.displayorder;
      const keyB = b.roletypeid + ',' + b.displayorder;
      return keyA.localeCompare(keyB);
    });

    const handleOnClick = (e: React.MouseEvent, name: string) => {
      e.preventDefault();
      e.stopPropagation();
      ccUtils.customSearch(ctx, '/record/creator,/record/conxother', '', '[' + name + ']', 'Personnes et institutions : ' + name);
    };

    const rows = elementArray.map((element: any, index) => {
      const { role, [tag]: tagValue, displaydate } = element;
      return (
        <div className={styles.row} key={index} style={{ marginLeft: '20px' }}>
          <span className={styles.taglabel}>{role} : </span>
          <span className={styles.authName} onClick={(e) => handleOnClick(e, tagValue)}>{tagValue}</span>
          {displaydate && <span className={styles.authDate}>&nbsp;({displaydate})</span>}
        </div>
      );
    });

    if (rows.length === 0) return null;

    return <>
      <div>
        {rows}
      </div>
    </>
  }

  function getAuteurs(record: any, tag: keyof typeof record.data.record) {
    const elements = record?.data?.record?.[tag];
    if (!elements) return null;

    const elementArray = Array.isArray(elements) ? elements : [elements];

    elementArray.sort((a, b) => {
      const keyA = a.RoleTypeID + ',' + a.DisplayOrder;
      const keyB = b.RoleTypeID + ',' + b.DisplayOrder;
      return keyA.localeCompare(keyB);
    });

    const handleOnClick = (e: React.MouseEvent, name: string) => {
      e.preventDefault();
      e.stopPropagation();
      ccUtils.customSearch(ctx, '/record/creator,/record/conxother', '', '[' + name + ']', 'Personnes et institutions : ' + name);
    };

    const rows = elementArray.map((element: any, index) => {
      const { Role, [tag]: tagValue, displaydate } = element;
      return (
        <div className={styles.row} key={index}>
          <span className={styles.taglabel}>{Role} : </span>
          <span className={styles.authName} onClick={(e) => handleOnClick(e, tagValue)}>{tagValue}</span>
          {displaydate && <span className={styles.authDate}>&nbsp;({displaydate})</span>}
        </div>
      );
    });

    if (rows.length === 0) return null;

    return <>
      <div>
        {rows}
      </div>
    </>
  }


  try {
    return (
      <div id="pdf-container" className={styles.Record}>
        <div className={`pdfOnly`} style={{ marginBottom: '20px' }}>
          {/*<img src='headerPdf.jpg' />*/}
          <div style={{textAlign: "center"}}>
            <h2>
            {getDefaultRow(record, "", "title.title", "Sans titre", "")}
            </h2>
            {/*noDziUrl && <img src={`${process.env.REACT_APP_IMAGE_BASE_URL}${noDziUrl}`} width="150"  alt="" crossOrigin="anonymous" />*/}
          </div>
        </div>

          <div className={styles.ccListviewRecordImage + " pdfCenter"}>
            <div className={styles.imgItemWrapper}>
              <span className={styles.imgItem + " " + styles.imgItemFirst}>
                <div className={styles.ZoomedImage} style={{textAlign: "center", marginLeft: "auto", marginRight: "auto"}}>
                  {!imageUrl && noImageUrl && <img src={noImageUrl} alt="non disponible" />}
                  {imageUrl && <ZoomedImage key={imageUrl} dzi={imageUrl} downloadAllowed={false} images={filmStripImages.map(img => ({ imagesmore: img.image, copyright: img.copyright, rank: img.rank }))} initialIndex={currentIndex} />}
                </div>
              </span>
            </div>
            {filmStripImages.length > 1 && (
              <div className={styles.filmStripContainer + " screenOnly"}>
                <div className={styles.buttonContainer}>
                  <GenericIconButton icon='light/angle-left.svg' width='22px' height='20px' onClick={handleScrollLeft} />
                </div>
                <div className={styles.filmStrip} ref={filmStripRef}>
                  {filmStripImages.map((img, idx) => (
                    <img
                      key={idx}
                      src={`${process.env.REACT_APP_IMAGE_BASE_URL}/${img.image}&height=100&bg=ffffff&cache=yes`}
                      alt={`Rank ${img.rank}`}
                      className={styles.filmStripImage}
                      onClick={() => handleFilmStripImageClick(img, idx)}
                      //onError={(e: React.SyntheticEvent<HTMLImageElement, Event>) => { leads to infinite loop
                      //  e.currentTarget.onerror = null;
                      //  e.currentTarget.src = noImageUrl || 'path_to_default_no_image.jpg'; // Ensure string fallback
                      //}}
                    />
                  ))}
                </div>
                <div className={styles.buttonContainer}>
                  <GenericIconButton icon='light/angle-right.svg' width='22px' height='20px' onClick={handleScrollRight} />
                </div>
              </div>
            )}
        </div>
        <div className={styles.ccListviewRecordText + " pdfUnder"}>
          <div className={styles.title + " screenOnly"}>
            {getDefaultRow(record, t("record_title"), "title.title")}
          </div>
          {getDefaultRow(record, t("record_objectnumber"), "objectnumber")}
          {/*getDefaultRowHyperlink(record, t("record_classification"), "classification.classification", "classification")*/}
          {getDefaultRow(record, t("record_commentairehtml"), "commentaire")}
          {getDefaultRowHyperlink(record, t("record_classification"), "classification")}

          {getComponents(record)}
          <ObjAssoc record={record} inheritance={record?.data?.record?.relatedchild ? 'child' : 'parent'}></ObjAssoc>

          {getDefaultRow(record, t("record_accessionmethod"), "accessionmethod")}
          {getDefaultRow(record, t("record_accessiondate"), "accessiondate")}

          <div className={styles.taglabel} style={{ marginTop: '5px', marginBottom: '10px' }}>
            {t("record_personnes_institutions")}
          </div>
          {getDefaultRowHyperlink(record, t("record_auteur"), "auteur.auteur", "auteur")}
          {getPersonnes(record, 'conxother')}
          {getAuteurs(record, 'creator')}
          {getHyperlinks(record)}
          {/*getDefaultRow(record, t("record_objectname"), "objectname")*/}
          {getDefaultRow(record, t("record_dated"), "dated")}
          {getDefaultRow(record, t("record_dimensions"), "dimensions")}
          {getDefaultRow(record, t("record_medium"), "medium")}
          {getDefaultRow(record, t("record_inscribed"), "inscribed")}
          {getDefaultRow(record, "Catalogue raisonné", "catrais")}
          {getDefaultRow(record, "Bibliographie ", "bibliography")}
          {/*getDefaultRow(record, "Documents accompagnant l'objet", "relatedworks")*/}
          {getDefaultRow(record, "Historique expositions", "exhibition")}
          {getDefaultRowHyperlinkArray(record, t("record_theme"), "thesthemes")}
          {getDefaultRow(record, t("record_location"), "locmuseesalle")}
          {getDefaultRow(record, "Exposition permanente", "locmuseecollection")}
          <div className={`screenOnly`}>
            <span className={styles.tagValue}>
              <strong>Permalien : </strong>
              {`${process.env.REACT_APP_BASE_URL}?id=${record?.data?.record?.objectnumber}`}
              <button className={genericStyles.GenericButton}
                onClick={() =>
                  navigator.clipboard.writeText(
                    `${process.env.REACT_APP_BASE_URL}?id=${record?.data?.record?.objectnumber}`
                  )
                }
              >
                Copie
              </button>
            </span>
            </div>
          {getDefaultRow(record, "Mise à jour de la notice", "catalogueisodate")}
          <div className={`screenOnly`}>
            {ctx.user && (
            <div className={styles.basketManagerContainer}>
              <BasketManager
                objectId={record?.data?.record?.ccobjectid}
              />
            </div>
            )}
          </div>
          <div className={`screenOnly`}>
          <ObjSameTheme record={record}></ObjSameTheme>
          </div>

        </div>
        </div>
    );
  } catch (e) {
    setError(e as any);
    return <div>Error: {(e as any).message}</div>;
  }
}

export default Record;
